import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import VideoCard from "@/components/base/VideoCard";
import EmptyState from "@/components/base/EmptyState";
import SectionHeading from "@/components/base/SectionHeading";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import {
  allCategories,
  getCategoryBySlug,
  getHotVideos,
  isWithinTime,
  searchVideos,
  timeFilterOptions,
} from "@/lib/video";

export default function SearchPage() {
  const [params, setParams] = useSearchParams();
  const query = params.get("q") ?? "";

  const [keyword, setKeyword] = useState(query);
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [timeFilter, setTimeFilter] = useState("all");

  useDocumentMeta({
    title: query ? `“${query}”的搜索结果 - 逼特逼视频` : "视频搜索 - 逼特逼视频",
    description: query
      ? `在逼特逼视频中搜索“${query}”的相关视频内容，涵盖影视剪辑、短视频与用户作品等多种类型。`
      : "在逼特逼视频中搜索你感兴趣的视频内容，支持按分类与上传时间筛选。",
    keywords: "逼特逼视频,视频搜索,短视频,影视剪辑",
  });

  useEffect(() => {
    setKeyword(query);
    setCategoryFilter("all");
    setTimeFilter("all");
  }, [query]);

  const results = useMemo(() => {
    const matched = searchVideos(query);
    return matched.filter(
      (v) =>
        (categoryFilter === "all" || v.category === categoryFilter) &&
        isWithinTime(v.uploadDate, timeFilter),
    );
  }, [query, categoryFilter, timeFilter]);

  const recommended = getHotVideos(4);

  const submitSearch = (event: React.FormEvent) => {
    event.preventDefault();
    const value = keyword.trim();
    setParams(value ? { q: value } : {});
  };

  return (
    <div className="mx-auto w-full max-w-[1440px] px-4 py-6 md:px-8">
      <section className="rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">
          搜索视频
        </h1>
        <p className="mt-2 text-sm text-foreground-600">
          输入关键词，从演示视频库中查找匹配的标题、标签与作者。
        </p>
        <form onSubmit={submitSearch} className="mt-5">
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-3 transition-colors focus-within:border-primary-400">
              <i className="ri-search-line text-lg text-foreground-400" />
              <input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                type="text"
                placeholder="例如：混剪、治愈、音乐现场"
                className="w-full bg-transparent text-sm text-foreground-900 outline-none placeholder:text-foreground-400"
              />
              {keyword && (
                <button
                  type="button"
                  onClick={() => setKeyword("")}
                  className="cursor-pointer text-foreground-400 transition-colors hover:text-foreground-700"
                  aria-label="清空"
                >
                  <i className="ri-close-circle-line text-lg" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="inline-flex cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line text-base" />
              搜索
            </button>
          </div>
        </form>

        {query && (
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <span className="flex items-center gap-1.5 whitespace-nowrap text-sm text-foreground-600">
              <i className="ri-filter-3-line" />
              筛选
            </span>
            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
            >
              <option value="all">全部分类</option>
              {allCategories.map((cat) => (
                <option key={cat.slug} value={cat.slug}>
                  {cat.name}
                </option>
              ))}
            </select>
            <select
              value={timeFilter}
              onChange={(e) => setTimeFilter(e.target.value)}
              className="cursor-pointer rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 outline-none transition-colors focus:border-primary-400"
            >
              {timeFilterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        )}
      </section>

      {!query ? (
        <div className="mt-8">
          <EmptyState
            icon="ri-search-2-line"
            title="输入关键词开始探索"
            description="本站搜索为前端演示功能，将匹配演示数据中的视频标题、标签与作者信息。"
          />
          <div className="mt-10">
            <SectionHeading title="热门推荐" subtitle="不知道看什么？先从这些开始" icon="ri-fire-line" />
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
              {recommended.map((video) => (
                <VideoCard key={video.id} video={video} />
              ))}
            </div>
          </div>
        </div>
      ) : (
        <div className="mt-8">
          <p className="mb-5 text-sm text-foreground-600">
            关键词「<span className="font-medium text-foreground-900">{query}</span>」共找到{" "}
            <span className="font-medium text-primary-600">{results.length}</span> 个视频
          </p>

          {results.length === 0 ? (
            <>
              <EmptyState
                icon="ri-emotion-sad-line"
                title="没有找到相关视频"
                description="换个关键词试试，或者看看下面的热门推荐内容。"
              />
              <div className="mt-10">
                <SectionHeading title="热门推荐" subtitle="这些内容同样精彩" icon="ri-fire-line" />
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  {recommended.map((video) => (
                    <VideoCard key={video.id} video={video} />
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {results.map((video) => (
                <VideoCard
                  key={video.id}
                  video={video}
                  categoryName={getCategoryBySlug(video.category)?.name}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <p className="mt-10 rounded-lg bg-secondary-50 px-4 py-3 text-center text-xs text-secondary-800">
        <i className="ri-information-line mr-1" />
        提示：本站搜索与筛选均为前端演示逻辑，不接入真实搜索接口。
        <Link to="/contact" className="ml-1 cursor-pointer underline">
          前往反馈
        </Link>
      </p>
    </div>
  );
}